import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Sprout } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, PageTitle } from "@/components/Layout";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export default function Rewards() {
  const { t } = useApp();
  const [d, setD] = useState(null);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/rewards").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const redeem = async () => {
    setBusy(true);
    try { await api.post(`/rewards/${pick.reward_id}/redeem`); toast.success(t("Reward redeemed!")); setPick(null); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return null;
  return (
    <div className="space-y-4">
      <PageTitle sub={t("Swap your Green Points for rewards. Items come with your next pickup.")}>{t("Rewards store")}</PageTitle>
      <div className="ks-grain flex items-center justify-between rounded-3xl bg-[#0D3B2B] p-5 text-white">
        <div><p className="text-xs uppercase tracking-widest text-white/70">{t("Available points")}</p><p className="font-display text-4xl font-extrabold" data-testid="rewards-balance">{d.balance}</p></div>
        <Sprout className="h-10 w-10 text-[#2E9E6B]" />
      </div>
      {d.items.map((r) => (
        <Card key={r.reward_id} className="flex items-center gap-4" data-testid={`reward-item-${r.reward_id}`}>
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#FDF0EC]"><Gift className="h-7 w-7 text-[#E05C3A]" /></div>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-[#0D3B2B]">{t(r.name)}</p>
            <p className="text-xs text-[#4A6358]">{t(r.description)}</p>
            <p className="mt-1 text-sm font-bold text-[#2E9E6B]">{r.cost} {t("points")}{r.stock != null && <span className="ml-2 text-xs font-normal text-[#7B968B]">{r.stock} {t("left")}</span>}</p>
          </div>
          <button onClick={() => setPick(r)} disabled={d.balance < r.cost || r.stock === 0} data-testid={`reward-redeem-${r.reward_id}`}
            className="h-11 shrink-0 rounded-xl bg-[#0D3B2B] px-4 text-sm font-bold text-white disabled:opacity-30">{t("Redeem")}</button>
        </Card>
      ))}
      {!d.items.length && <Card data-testid="rewards-empty" className="text-[#4A6358]">{t("New rewards are coming soon. Keep collecting Green Points!")}</Card>}
      {!!d.mine.length && (
        <Card className="space-y-2" data-testid="rewards-mine">
          <p className="font-display font-bold text-[#0D3B2B]">{t("My redemptions")}</p>
          {d.mine.map((x) => (
            <div key={x.redemption_id} className="flex justify-between text-sm" data-testid={`redemption-${x.redemption_id}`}>
              <span>{t(x.name)} · {x.cost} {t("pts")}<br /><span className="text-xs text-[#7B968B]">{fmtDateTime(x.created_at)}</span></span>
              <span className={`text-xs font-bold ${x.status === "Delivered" ? "text-[#2E9E6B]" : x.status === "Cancelled" ? "text-[#7B968B]" : "text-[#E05C3A]"}`}>{t(x.status)}</span>
            </div>
          ))}
        </Card>
      )}
      <ConfirmDialog open={!!pick} onOpenChange={(o) => !o && setPick(null)} title={t("Redeem this reward?")} busy={busy} onYes={redeem}
        lines={pick ? [`${t(pick.name)} · ${pick.cost} ${t("points")}`] : []} />
    </div>
  );
}
